import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { LoggedUser } from '../authentication/authentication';
import { LOCAL_STORAGE_KEYS, LocalStorageService } from '../local-storage.service';
import { LoggerService } from '../logger/logger.service';
import { isClient } from './guards';
import { SignalsService } from '../signals.service';
import { RouteReloadService } from '../reload-current-route';
import { NativeCheckService } from '../native-check.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private _loggedUser: LoggedUser | null = null;
  private _bearer = '';

  public asyncUserDataUpdating = new BehaviorSubject<boolean>(true);
  public hasAnyPortfolioOrStrategy = true;

  public refreshTokenUpdating(): Observable<boolean> {
    return this.asyncUserDataUpdating.asObservable();
  }

  public get bearer(): string {
    return this._bearer;
  }

  public get loggedUser(): LoggedUser | null {
    return this._loggedUser;
  }

  public set loggedUser(value: LoggedUser | null) {
    this._loggedUser = value;
    this.saveUserToLocalStorage();
  }

  public constructor(
    private localStorageService: LocalStorageService,
    private loggerService: LoggerService,
    private signalsService: SignalsService,
    private routeReloadService: RouteReloadService,
    private nativeCheckService: NativeCheckService,
  ) {

    const localDataReqests = [
      this.localStorageService.getItem({key: LOCAL_STORAGE_KEYS.BEARER_STORAGE_KEY}),
      this.localStorageService.getItem({key: LOCAL_STORAGE_KEYS.USER_LOCAL_STORAGE_KEY}),
    ];

    forkJoin(localDataReqests).pipe(
      take(1),
      map(
        ([ bearer, userData ]) =>
        ({ bearer, userData })
      ),
    ).subscribe({
        error: this.handleInitError.bind(this),
        next: this.handleInitData.bind(this),
    });

    // watch for storage data change, log/logout user in multiple browser tabs/windows
    if(!this.nativeCheckService.isNativePlatform()) {
      window.addEventListener('storage', this.storageChanged.bind(this));
    }
  }

  private handleInitError(error: unknown): void {
    this.asyncUserDataUpdating.next(false);
    this.loggerService.error('Failed to load local data. ', error);
  }

  private handleInitData(data: { bearer: string , userData: string }): void {
    this._bearer = data.bearer;
    try {
      if(data.userData && !this._loggedUser) {
        this._loggedUser = JSON.parse(data.userData);
      }
    } catch (error) {
      this.loggerService.error('Failed to parse local data. ', error);
    }
    this.asyncUserDataUpdating.next(false);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public removeUserFromLocal(): Observable<any> {
      this.loggedUser = null;
      this._bearer = '';
    return forkJoin([
      this.localStorageService.deleteItem({ key: LOCAL_STORAGE_KEYS.TOKEN_LOCAL_STORAGE_KEY }),
      this.localStorageService.deleteItem({ key: LOCAL_STORAGE_KEYS.TOKEN_EXPIRES_LOCAL_STORAGE_KEY }),
      this.localStorageService.deleteItem({ key: LOCAL_STORAGE_KEYS.LOCAL_STORAGE_TIMER_KEY }),
      this.localStorageService.deleteItem({ key: LOCAL_STORAGE_KEYS.USER_LOCAL_STORAGE_KEY }),
      this.localStorageService.deleteItem({ key: LOCAL_STORAGE_KEYS.BEARER_STORAGE_KEY }),
    ]);
  }

  private saveUserToLocalStorage(): void {
    if (this.loggedUser) {
      this.localStorageService.setItem({
        key: LOCAL_STORAGE_KEYS.USER_LOCAL_STORAGE_KEY,
        value: JSON.stringify(this._loggedUser)
      });
      this.signalsService.triggerUserUpdate();
    }
  }

  public updateBearer(newToken: string): void {
    this._bearer = newToken;
    this.localStorageService.setItem({
      key: LOCAL_STORAGE_KEYS.BEARER_STORAGE_KEY,
      value: newToken,
    });
  }

  private storageChanged(event: StorageEvent): void {
    if(
      (event.key === LOCAL_STORAGE_KEYS.USER_LOCAL_STORAGE_KEY || event.key === LOCAL_STORAGE_KEYS.BEARER_STORAGE_KEY) &&
      typeof event.newValue === 'string' &&
      event.newValue.includes('{')
    ) {
      const storageBearerToken = this.localStorageService.getItemSync({ key: LOCAL_STORAGE_KEYS.BEARER_STORAGE_KEY });

      // if attempted to redirect without Bearer would throw 401 and logout both windows
      if(!storageBearerToken || storageBearerToken === null) return;

      try {
        const parsedEventValue = JSON.parse(event.newValue);
        const client = isClient(parsedEventValue);

        if (client) {
          this._loggedUser = client;
          this._bearer = storageBearerToken;
          this.routeReloadService.reloadCurrent();
        }
      } catch (error) {
        console.error('Invalid object from local data.');
        this.loggerService.error(error);
      }
    }
  }
}
